// Wormhole subspace bonus level: a timed formation assault on ANOTHER plane
// with a rare/mythical boon reward. Phases: entering (warp-in tunnel; the
// entrance wormhole collapses behind the player) → fighting (seeded formation
// chain against a 30 s timer / 50-kill target; the exit portal is always
// available — fly into it to escape with your points but no boon) → boon
// (upgrade overlay, 3 rare/mythical choices) → exiting (warp-out tunnel; the
// player returns to return_pos) → idle.
//
// Containment is by construction (spec .agent/tasks/wormhole-subspace):
// start() swaps world.enemies to the dedicated subspace pool, so normal
// enemies are never simulated or rendered during the trip and resume
// byte-identical; subspace attackers are allocated only from that pool and
// survivors dissipate when it is cleared on exit. Formations live in
// sim/subspace.ts.
//
// Deterministic: formation chain, portal placement, and boon draws come from
// private mulberry32 streams derived from the run seed; no wall-clock
// (GDD §A.1).

import type { WorldState } from './state.js';
import type { BonusGameState, BonusPhase } from '../types.js';
import { wrappedDelta } from './collision.js';
import { RngStream } from './rng.js';
import { SubspaceDirector, SUBSPACE_KILL_SCORE } from './subspace.js';
import {
  applyUpgradeToPlayer,
  isUpgradeOfferEligible,
  type UpgradeChoice,
  type UpgradeDef,
  type UpgradeRarity
} from './upgrade-system.js';
import upgradesData from '../content/upgrades.json';

/** Fighting phase ends in success after 30 s at 60 Hz … */
export const BONUS_TIMER_TICKS = 30 * 60;
/** … or immediately when this many enemies are killed. */
export const BONUS_KILL_TARGET = 50;
/**
 * Warp transition durations (60 Hz): 1.1 s into subspace, 0.9 s back out.
 * The renderer reads bonus.phase + phase_start_tick and maps progress with
 * these constants — sim owns the timing, render owns the tunnel.
 */
export const WARP_IN_TICKS = 66;
export const WARP_OUT_TICKS = 54;

// Private stream salts: subspace layout and boon draws stay independent of
// the shared world streams and of each other (UpgradeSystem-salt pattern).
const BONUS_STREAM_SALT = 0xC2B2AE35;
const BOON_STREAM_SALT = 0x7F4A7C15;

const UPGRADE_DEFS = (upgradesData as { version: string; entries: UpgradeDef[] }).entries;
/** Boons only ever offer rare/mythical upgrades. */
const BOON_RARITIES: readonly UpgradeRarity[] = ['rare', 'mythical'];

export function createBonusGameState(): BonusGameState {
  return {
    phase: 'idle',
    phase_start_tick: 0,
    timer_until_tick: 0,
    kill_target: BONUS_KILL_TARGET,
    kills: 0,
    return_pos: { x: 0, y: 0 },
    subspace_score: 0,
    formation_id: '',
    formation_index: 0,
    exit_portal: { active: false, pos: { x: 0, y: 0 }, radius: 0, collapse_tick: -1 }
  };
}

/**
 * Per-run subspace bonus controller. Serializable phase state lives on
 * `world.bonus`; this class owns the private RNG streams, the formation
 * director, the pending boon offer, and boon-granted rank bookkeeping.
 * Mirrors the UpgradeSystem lifecycle: constructed once, `reset()` after
 * every `world.reset(seed)`.
 */
export class BonusGame {
  private readonly world: WorldState;
  /** Rank lookup into the Lumensurge system so boons respect max_rank. */
  private readonly rankOf: (id: string) => number;
  private stream: RngStream;
  private boonStream: RngStream;
  /** Ranks granted by boon picks (UpgradeSystem only tracks its own offers). */
  private boonRanks = new Map<string, number>();
  private pendingBoon: UpgradeChoice[] | null = null;
  /** Formation director for the current trip (seeded off the bonus stream). */
  private subspace: SubspaceDirector;

  constructor(world: WorldState, rankOf: (id: string) => number) {
    this.world = world;
    this.rankOf = rankOf;
    this.stream = new RngStream((world.runSeed ^ BONUS_STREAM_SALT) >>> 0);
    this.boonStream = new RngStream((world.runSeed ^ BOON_STREAM_SALT) >>> 0);
    this.subspace = new SubspaceDirector(world, this.stream);
    // Subspace kill feed: only fighting-phase kills count toward the
    // objective, and each banks the multiplier value as trip score. Registered
    // once — EventBus.clear() on reset keeps handlers, so this rides every run.
    // (During subspace the normal pool is detached, so a fighting-phase enemy
    // death is a subspace kill by construction.)
    world.events.register((e) => {
      if (e.type !== 'death' || e.payload.kind !== 'enemy') return;
      const b = this.world.bonus;
      if (b.phase !== 'fighting') return;
      b.kills++;
      b.subspace_score += SUBSPACE_KILL_SCORE;
    });
  }

  /** Reset per-run state. Call after `world.reset(seed)` on (re)start. */
  reset(): void {
    this.stream = new RngStream((this.world.runSeed ^ BONUS_STREAM_SALT) >>> 0);
    this.boonStream = new RngStream((this.world.runSeed ^ BOON_STREAM_SALT) >>> 0);
    this.subspace = new SubspaceDirector(this.world, this.stream);
    this.boonRanks.clear();
    this.pendingBoon = null;
  }

  get phase(): BonusPhase {
    return this.world.bonus.phase;
  }

  /**
   * Enter subspace: save the player position, suspend the normal plane (pool
   * swap — the relocation IS the plane switch; the player keeps their
   * coordinates), open the exit portal, arm the objective, and field the
   * opening formation so it materializes as the warp-in completes.
   */
  start(tick: number): void {
    const b = this.world.bonus;
    if (b.phase !== 'idle') return;
    if (this.world.inSubspace) return; // belt-and-suspenders: never double-dip
    const player = this.world.player;
    b.return_pos = { x: player.pos.x, y: player.pos.y };
    b.kills = 0;
    b.kill_target = BONUS_KILL_TARGET;
    b.timer_until_tick = tick + BONUS_TIMER_TICKS;
    b.subspace_score = 0;
    b.formation_id = '';
    b.formation_index = 0;
    b.exit_portal = { active: false, pos: { x: 0, y: 0 }, radius: 0, collapse_tick: -1 };

    this.world.enterSubspace();
    this.subspace.reset();
    this.subspace.spawnExitPortal(tick);
    this.setPhase('entering', tick);
    // Opening wave: materializes exactly as the warp-in ends.
    this.subspace.spawnNext(tick, tick + WARP_IN_TICKS);
  }

  /**
   * Per-tick state machine + subspace director update. Call BEFORE
   * updateEnemies: formation drives are written here and read by the enemy
   * steering pass of the same tick.
   */
  update(tick: number): void {
    const b = this.world.bonus;
    // Run over mid-trip: bail out of subspace immediately so the defeat
    // cinematic plays over the restored arena (and no boon phase can wedge
    // behind the game-over panel).
    if (this.world.gameOver && b.phase !== 'idle' && b.phase !== 'exiting') {
      this.beginExit(tick);
      return;
    }
    switch (b.phase) {
      case 'entering':
        this.subspace.update(tick);
        this.checkExitPortal(tick);
        if (tick - b.phase_start_tick >= WARP_IN_TICKS) this.setPhase('fighting', tick);
        break;
      case 'fighting':
        this.subspace.update(tick);
        this.checkExitPortal(tick);
        if (b.phase !== 'fighting') break; // escaped through the exit portal
        if (b.kills >= b.kill_target || tick >= b.timer_until_tick) {
          this.pendingBoon = this.generateBoonChoices();
          if (this.pendingBoon.length > 0) {
            this.setPhase('boon', tick);
            this.world.events.emit(
              'levelup_offer',
              { boon: true, choices: this.pendingBoon.map((c) => c.def.id) },
              tick
            );
          } else {
            // Every rare/mythical upgrade is maxed: skip the boon and exit.
            this.beginExit(tick);
          }
        }
        break;
      case 'boon':
        // Waiting on chooseBoon() from the upgrade overlay.
        break;
      case 'exiting':
        if (tick - b.phase_start_tick >= WARP_OUT_TICKS) this.setPhase('idle', tick);
        break;
    }
  }

  /** Boon choices for the upgrade overlay (empty unless phase === 'boon'). */
  getBoonChoices(): readonly UpgradeChoice[] {
    return this.pendingBoon ?? [];
  }

  /**
   * Apply the boon pick and exit subspace: the player returns to the
   * pre-entry position and normal spawning resumes after the warp-out.
   */
  chooseBoon(index: number, tick: number): UpgradeChoice | null {
    const choice = this.pendingBoon?.[index];
    if (!choice) return null;
    this.pendingBoon = null;
    this.boonRanks.set(choice.def.id, (this.boonRanks.get(choice.def.id) ?? 0) + 1);
    applyUpgradeToPlayer(this.world.player, choice.def);
    this.world.events.emit(
      'levelup_pick',
      { boon: true, upgrade_id: choice.def.id, rank: choice.nextRank },
      tick
    );
    this.beginExit(tick);
    return choice;
  }

  /**
   * Escape hatch: player contact with the exit portal. Keeps the banked
   * subspace score, forfeits the completion boon (no boon phase), collapses
   * the portal behind them.
   */
  private checkExitPortal(tick: number): void {
    const portal = this.world.bonus.exit_portal;
    if (!portal.active) return;
    const player = this.world.player;
    if (!player.alive) return;
    const d = wrappedDelta(player.pos, portal.pos);
    const r = portal.radius + player.hitbox_radius;
    if (d.x * d.x + d.y * d.y > r * r) return;
    this.world.events.emit('phase_change', { system: 'bonus', phase: 'escape' }, tick);
    this.beginExit(tick);
  }

  /**
   * Leave subspace (boon granted OR escape): collapse the exit portal,
   * dissipate the surviving attackers, restore the normal plane exactly as it
   * was, and put the player back at the pre-entry position. The warp-out
   * tunnel plays over the restored arena during 'exiting'.
   */
  private beginExit(tick: number): void {
    const b = this.world.bonus;
    const portal = b.exit_portal;
    if (portal.active) {
      portal.active = false;
      portal.collapse_tick = tick;
    }
    this.world.exitSubspace();
    const player = this.world.player;
    player.pos = { x: b.return_pos.x, y: b.return_pos.y };
    player.vel = { x: 0, y: 0 };
    this.setPhase('exiting', tick);
  }

  private setPhase(phase: BonusPhase, tick: number): void {
    const b = this.world.bonus;
    b.phase = phase;
    b.phase_start_tick = tick;
    this.world.events.emit('phase_change', { system: 'bonus', phase }, tick);
  }

  /** Draw up to 3 distinct rare/mythical upgrades from the private boon stream. */
  private generateBoonChoices(): UpgradeChoice[] {
    // Same eligibility as the Lumensurge draw: no weapon boons into full
    // hardpoints, no HARDPOINT +1 at the slot cap. Boon ranks stack onto the
    // Lumensurge rank for the max_rank guard.
    const eligible = UPGRADE_DEFS.filter(
      (d) =>
        BOON_RARITIES.includes(d.rarity) &&
        isUpgradeOfferEligible(
          this.world.player,
          d,
          this.rankOf(d.id) + (this.boonRanks.get(d.id) ?? 0)
        )
    );
    const choices: UpgradeChoice[] = [];
    const count = Math.min(3, eligible.length);
    for (let i = 0; i < count; i++) {
      const def = eligible.splice(this.boonStream.next_int(eligible.length), 1)[0];
      const nextRank = this.rankOf(def.id) + (this.boonRanks.get(def.id) ?? 0) + 1;
      choices.push({ def, nextRank });
    }
    return choices;
  }
}
